import * as React from 'react'

import {
  Box,
  Button,
  Icon,
  Text,
  Touchable,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import competencies from 'modules/competencies/store/collections/competencies'
import competencyOptions from 'modules/competencies/store/collections/competencyOptions'
import PerformanceRatingInput from 'modules/performance/components/PerformanceRatingInput'
import QuestionPosition from 'modules/reviews/components/WriteResponse/Question/QuestionPosition'
import { useQuestionGroupCompetencies } from 'modules/reviews/hooks/useQuestionGroupsCompetencies'
import type { CompetencyAssessment } from 'modules/reviews/store/models/QuestionGroup'

import CommentModal from '../CommentModal'
import CompetencyDetail from '../CompetencyDetail'
import AssessmentItemLoadingState from './AssessmentItemLoadingState'

interface Props {
  assessment: CompetencyAssessment
  readonly: boolean
  position: number
  loading?: boolean
  onUpdate?: (assessment: CompetencyAssessment) => void
  accessCompetencies: boolean
}

const AssessmentItem: React.FC<Props> = ({
  assessment,
  readonly,
  position,
  onUpdate,
  loading,
  accessCompetencies,
}) => {
  const { loading: loadingCompetenciesAvailability } =
    useQuestionGroupCompetencies(accessCompetencies)
  const competency = competencies.get(assessment.competency_id)
  const competencyOption = competencyOptions.get(assessment.competency_option_id)

  const [isDetailOpen, toggleDetail] = useToggle(false)
  const [isCommentOpen, toggleComment] = useToggle(false)

  if (loading || loadingCompetenciesAvailability) {
    return <AssessmentItemLoadingState />
  }

  if (!competency) {
    return null
  }

  const options = competency.options
  const minRange = competency?.range?.[0]
  const maxRange = competency?.range?.[1]
  const showDetail = competency.attributes.description || !!competency.optionsWithDescription.length

  if (typeof minRange === 'undefined' || typeof maxRange === 'undefined') {
    return null
  }

  const onClick = (value: number) => {
    const selectedOption = options.find((option) => option.value === value)
    if (selectedOption && onUpdate) {
      onUpdate({ ...assessment, competency_option_id: selectedOption.id })
    }
  }

  const handleCommentChange = (comment: string) => {
    if (onUpdate) {
      onUpdate({ ...assessment, description: comment })
    }
  }

  return (
    <>
      <Box flexDirection="row" gap="s16" alignItems="baseline">
        <Box flexNoShrink>
          <QuestionPosition position={position} />
        </Box>
        <Box flexGrow gap="s16">
          <Box flexDirection="row" alignItems="center" flexGrow>
            <Box gap="s12" flexGrow flexDirection="row" alignItems="center">
              <Text.Normal size="200" weight="medium" color="grey800" whiteSpace="breakSpaces">
                {competency.attributes.name}
              </Text.Normal>
              {showDetail && (
                <Touchable onClick={toggleDetail(true)}>
                  <Icon.Medium icon="info-circle-line" color="grey800" />
                </Touchable>
              )}
            </Box>
            <Box
              flexDirection="row"
              justifyContent="flexEnd"
              aria-label="assessment-competency-comment"
            >
              <Button
                type="secondary"
                onClick={toggleComment(true)}
                iconName={assessment.description ? 'file-filled' : 'file'}
              ></Button>
              {isCommentOpen && (
                <CommentModal
                  competencyName={competency.attributes.name}
                  onClose={toggleComment(false)}
                  comment={assessment.description || ''}
                  onSubmit={handleCommentChange}
                  readonly={readonly}
                />
              )}
            </Box>
          </Box>
          <Box>
            <PerformanceRatingInput
              max={maxRange}
              min={minRange}
              onClick={onClick}
              readonly={readonly}
              value={competencyOption?.value}
              mandatory={competencyOption === undefined}
            />
          </Box>
        </Box>
      </Box>
      {isDetailOpen && <CompetencyDetail competency={competency} onClose={toggleDetail(false)} />}
    </>
  )
}

export default observer(AssessmentItem)
